/**
 * PermissionGuard Component
 * 
 * Conditionally renders children based on user permissions.
 * Used for role-based access control in the UI.
 */

import React from 'react';
import { Box, Typography, Paper, Alert } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { usePermissions, type PermissionPath } from '../hooks/usePermissions';
import type { EmployeeRole, EmployeePermissions } from '../types/employee';

interface PermissionGuardProps {
    /**
     * Single permission path required (e.g., 'vehicles.create')
     */
    permission?: PermissionPath;

    /**
     * Multiple permissions - user must have ALL of these
     */
    permissions?: PermissionPath[];

    /**
     * Multiple permissions - user must have AT LEAST ONE of these
     */
    anyPermission?: PermissionPath[];

    /**
     * Required role or higher in hierarchy
     */
    role?: EmployeeRole;

    /**
     * Required category access (any permission in category)
     */
    category?: keyof EmployeePermissions;

    /**
     * Content to render when user has permission
     */
    children: React.ReactNode;

    /**
     * What to render when permission is denied
     * - 'nothing': Renders null (default)
     * - 'message': Shows access denied message
     * - 'disabled': Shows children but disabled
     * - React node: Custom fallback content
     */
    fallback?: 'nothing' | 'message' | 'disabled' | React.ReactNode;

    /**
     * Custom message for access denied
     */
    deniedMessage?: string;

    /**
     * Show in inline or block mode
     */
    inline?: boolean;
}

/**
 * Default access denied component
 */
const AccessDenied: React.FC<{ message?: string; inline?: boolean }> = ({
    message = 'Vous n avez pas l autorisation d acceder a cette fonctionnalite.',
    inline = false
}) => {
    if (inline) {
        return (
            <Typography
                component="span"
                color="text.disabled"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
                <LockIcon fontSize="small" />
                Acces refuse
            </Typography>
        );
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                textAlign: 'center',
                bgcolor: 'action.hover',
                borderRadius: 2
            }}
        >
            <LockIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
                {message}
            </Typography>
        </Paper>
    );
};

/**
 * PermissionGuard Component
 * 
 * Usage examples:
 * 
 * ```tsx
 * // Single permission
 * <PermissionGuard permission="vehicles.create">
 *   <Button>Ajouter un vehicule</Button>
 * </PermissionGuard>
 * 
 * // Multiple permissions (ALL required)
 * <PermissionGuard permissions={['finances.view', 'finances.transactions']}>
 *   <TransactionForm />
 * </PermissionGuard>
 * 
 * // Multiple permissions (ANY one)
 * <PermissionGuard anyPermission={['bookings.edit', 'bookings.approve']}>
 *   <BookingActions />
 * </PermissionGuard>
 * 
 * // Role-based
 * <PermissionGuard role="manager">
 *   <ManagerDashboard />
 * </PermissionGuard>
 * 
 * // With custom fallback
 * <PermissionGuard permission="settings.edit" fallback="message">
 *   <SettingsForm />
 * </PermissionGuard>
 * ```
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
    permission,
    permissions,
    anyPermission,
    role,
    category,
    children,
    fallback = 'nothing',
    deniedMessage,
    inline = false
}) => {
    const {
        hasPermission,
        hasAllPermissions,
        hasAnyPermission,
        hasRole,
        hasCategoryAccess,
        isOwner
    } = usePermissions();

    // Check all permission conditions
    const hasAccess = React.useMemo(() => {
        // Owners always have access
        if (isOwner) return true;

        // Check single permission
        if (permission && !hasPermission(permission)) return false;

        // Check all permissions
        if (permissions && permissions.length > 0 && !hasAllPermissions(permissions)) return false;

        // Check any permission
        if (anyPermission && anyPermission.length > 0 && !hasAnyPermission(anyPermission)) return false;

        // Check role
        if (role && !hasRole(role)) return false;

        // Check category access
        if (category && !hasCategoryAccess(category)) return false;

        return true;
    }, [permission, permissions, anyPermission, role, category, isOwner, hasPermission, hasAllPermissions, hasAnyPermission, hasRole, hasCategoryAccess]);

    // If user has access, render children
    if (hasAccess) {
        return <>{children}</>;
    }

    // Handle fallback
    if (fallback === 'nothing') {
        return null;
    }

    if (fallback === 'message') {
        return <AccessDenied message={deniedMessage} inline={inline} />;
    }

    if (fallback === 'disabled') {
        return (
            <Box sx={{ opacity: 0.5, pointerEvents: 'none', cursor: 'not-allowed' }}>
                {children}
            </Box>
        );
    }

    // Custom fallback (React node)
    return <>{fallback}</>;
};

/**
 * Higher-order component version of PermissionGuard
 */
export function withPermission<P extends object>(
    Component: React.ComponentType<P>,
    guardProps: Omit<PermissionGuardProps, 'children'>
) {
    const WrappedComponent: React.FC<P> = (props) => (
        <PermissionGuard {...guardProps}>
            <Component {...props} />
        </PermissionGuard>
    );

    WrappedComponent.displayName = `withPermission(${Component.displayName || Component.name || 'Component'})`;

    return WrappedComponent;
}

/**
 * Simple permission check component that just shows/hides content
 */
export const CanAccess: React.FC<{
    permission: PermissionPath;
    children: React.ReactNode;
}> = ({ permission, children }) => (
    <PermissionGuard permission={permission}>
        {children}
    </PermissionGuard>
);

/**
 * Role-based access component
 */
export const RequireRole: React.FC<{
    role: EmployeeRole;
    children: React.ReactNode;
    fallback?: React.ReactNode;
}> = ({ role, children, fallback }) => (
    <PermissionGuard role={role} fallback={fallback || 'nothing'}>
        {children}
    </PermissionGuard>
);

/**
 * Owner-only access component
 */
export const OwnerOnly: React.FC<{
    children: React.ReactNode;
    fallback?: React.ReactNode;
}> = ({ children, fallback }) => (
    <PermissionGuard role="owner" fallback={fallback || 'nothing'}>
        {children}
    </PermissionGuard>
);

/**
 * Manager or higher access component
 */
export const ManagerOnly: React.FC<{
    children: React.ReactNode;
    fallback?: React.ReactNode;
}> = ({ children, fallback }) => (
    <PermissionGuard role="manager" fallback={fallback || 'nothing'}>
        {children}
    </PermissionGuard>
);

/**
 * Access denied page component for protected routes
 */
export const AccessDeniedPage: React.FC<{
    message?: string;
    showBackButton?: boolean;
    onBack?: () => void;
}> = ({
    message = 'Vous n avez pas l autorisation d acceder a cette page.',
    showBackButton = true,
    onBack
}) => {
        const handleBack = () => {
            if (onBack) {
                onBack();
            } else {
                window.history.back();
            }
        };

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '60vh',
                    p: 3
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        maxWidth: 400,
                        borderRadius: 2
                    }}
                >
                    <LockIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
                    <Typography variant="h5" gutterBottom>
                        Acces refuse
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        {message}
                    </Typography>
                    {showBackButton && (
                        <Alert
                            severity="info"
                            sx={{ cursor: 'pointer' }}
                            onClick={handleBack}
                        >
                            Cliquer ici pour revenir
                        </Alert>
                    )}
                </Paper>
            </Box>
        );
    };

export default PermissionGuard;
